/**
 * The Bana GTM events: the client's tracking spec for the IC and EM application flows of IC-model
 * countries, sent next to the legacy events.
 *
 * Every event goes through `pushBanaEvent()`, which reuses the event push of the GoogleTagManager
 * instance, so the Bana events share the queue and consent handling of the legacy events.
 */
import { googleTagManager } from './index';
import { getSessionItem, setSessionItem } from '../session-storage';

export const BUSINESS_MODEL = Object.freeze({ IC: 'IC', EM: 'EM' });

// GTM `page_group` of the Bana screens
export const BANA_PAGE_GROUP = Object.freeze({
    GENERAL_LANDING: 'General landing page',
    LANDING: 'Landing page',
    WHAT_YOU_NEED: 'What you need',
    CITY_SELECTION: 'City selection',
    DELIVERY_AREA_FULL: 'Delivery area full',
    PERSONAL_INFORMATION: 'Personal information',
    REFERRAL: 'Personal information: referral',
    AGE: 'Personal information: age',
    HOURS: 'Schedule selection: hours',
    SHIFTS: 'Schedule selection: shifts',
    VEHICLE_SELECTION: 'Vehicle selection',
    APPLICATION_SUMMARY: 'Application summary',
    APPLICATION_SUMMARY_UNDER_AGE: 'Application summary: under legal age',
    APPLICATION_PROCESSING: 'Application processing',
    APPLICATION_SUBMITTED: 'Application submitted',
    APPLICATION_SUBMITTED_SUCCESS: 'Application submitted: success',
    APPLICATION_SUBMITTED_REJECTED: 'Application submitted: rejected',
    APPLICATION_SUBMITTED_SF_FAILED: 'Application submitted: sf_failed',
    WAITLIST_PERSONAL_INFORMATION: 'Waitlist: personal information',
    WAITLIST_VEHICLE_SELECTION: 'Waitlist: vehicle selection',
    WAITLIST_APPLICATION_SUMMARY: 'Waitlist: application summary',
    WAITLIST_APPLICATION_SUBMITTED: 'Waitlist: application submitted',
});

// Status of a Bana event in the dev console log
const BANA_STATUS = Object.freeze({
    SENT: 'SENT',
    QUEUED: 'QUEUED (waiting for the cookie banner)',
    GTM_DISABLED: 'GTM disabled (dataLayer only)',
    NO_EVENT: 'NO EVENT for this screen or outcome',
    DUPLICATE: 'SKIPPED: duplicate application_id',
});

// GTM `page_environment` of each APP_ENV
const BANA_PAGE_ENVIRONMENTS = Object.freeze({
    local: 'dev',
    dev: 'dev',
    testing: 'dev',
    acceptance: 'acceptance',
    review: 'acceptance',
    staging: 'staging',
    production: 'production',
});

// Query parameters kept in original_referrer / original_location; everything else (email, tokens, ...) is dropped
const BANA_URL_QUERY_ALLOWLIST = ['utm_*', 'gclid', 'fbclid', 'city', 'page', 'campaign', 'flow'];

// Employee form questions with a screen of their own (other questions, e.g. driving_license, have no page view)
const BANA_EM_QUESTION_PAGE_GROUPS = Object.freeze({
    age: BANA_PAGE_GROUP.AGE,
    agespec: BANA_PAGE_GROUP.AGE,
    working_hours: BANA_PAGE_GROUP.HOURS,
    picked_shift: BANA_PAGE_GROUP.SHIFTS,
    vehicle_type: BANA_PAGE_GROUP.VEHICLE_SELECTION,
});

// Outcome of a submission; each sends its own application_submitted (the IC waitlist sends none)
const BANA_OUTCOME = Object.freeze({
    IC_SUCCESS: 'ic_success',
    IC_WAITLIST: 'ic_waitlist',
    EM_SUCCESS: 'em_success',
    EM_CLOSED_CITY: 'em_closed_city',
    EM_UNDER_AGE: 'em_under_age',
});

// Session handoffs; outcomes and application ids only, never personal data
const BANA_OUTCOME_KEY = 'bana_outcome';
const BANA_TRACKED_SUBMISSIONS_KEY = 'bana_tracked_submissions';

//Last Bana screen tracked on this page (re-renders of the same screen are one page view)
let lastBanaScreen = null;
//Whether the Bana events are logged to the console on this page (dev only, evaluated once)
let banaDebugEnabled = null;

/**
 * Gate for the Bana events: the country uses the IC model (`GlobalDataProvider::useBanaTracking()`).
 * @returns {boolean}
 */
export function usesBanaTracking() {
    return window.useBanaTracking === true;
}

/**
 * The page_environment of this APP_ENV: dev, acceptance, staging or production.
 * @returns {string}
 */
function getBanaPageEnvironment() {
    return BANA_PAGE_ENVIRONMENTS[window.appEnvironment] ?? window.appEnvironment;
}

/**
 * Keep origin and path plus allowlisted query parameters, drop everything else (email, tokens,
 * Salesforce ids, ...) and the hash.
 * @param {string|null} url
 * @returns {string|null}
 */
function sanitiseBanaUrl(url) {
    if (!url) {
        return null;
    }

    let parsed;
    try {
        parsed = new URL(url);
    } catch {
        return null;
    }

    const isAllowed = (name) =>
        BANA_URL_QUERY_ALLOWLIST.some((entry) =>
            entry.endsWith('*') ? name.startsWith(entry.slice(0, -1)) : name === entry,
        );

    const kept = new URLSearchParams();
    parsed.searchParams.forEach((value, name) => {
        if (isAllowed(name)) {
            kept.append(name, value);
        }
    });
    const query = kept.toString();

    return parsed.origin + parsed.pathname + (query ? `?${query}` : '');
}

/**
 * Referrer and URL of the first page of the session, as stored by the GoogleTagManager's
 * captureOriginalData() (also run here when GTM is disabled, e.g. on localhost), sanitised.
 * @returns {{ original_referrer: string|null, original_location: string|null }}
 */
function getBanaOriginalData() {
    if (getSessionItem('original_location') === null) {
        googleTagManager.captureOriginalData();
    }

    return {
        original_referrer: sanitiseBanaUrl(getSessionItem('original_referrer') || null),
        original_location: sanitiseBanaUrl(getSessionItem('original_location') || null),
    };
}

/**
 * Values every Bana event sends, like setPageData() for the legacy events; each event adds its own.
 * @param {string} pageGroup - One of BANA_PAGE_GROUP.
 * @param {string|null} businessModel - One of BUSINESS_MODEL; none on the general landing page.
 * @returns {Object}
 */
function getBanaPageData(pageGroup, businessModel) {
    return {
        page_group: pageGroup,
        ...(businessModel && { business_model: businessModel }),
        country: window.country.code,
        page_environment: getBanaPageEnvironment(),
    };
}

/**
 * Log a Bana event to the console, to check the events by hand on localhost (where GTM is disabled).
 * Only in the dev environment (APP_ENV local / dev / testing); logs only what goes into the dataLayer.
 * @param {string} eventName
 * @param {Object|null} customData
 * @param {string} status - One of BANA_STATUS.
 */
function logBanaEvent(eventName, customData, status) {
    if (banaDebugEnabled === null) {
        banaDebugEnabled = getBanaPageEnvironment() === 'dev';
        if (banaDebugEnabled) {
            window.banaEvents = [];
        }
    }
    if (!banaDebugEnabled) {
        return;
    }

    const pageGroup = customData?.page_group ?? '—';
    const businessModel = customData?.business_model ?? '—';
    window.banaEvents.push({
        at: new Date().toISOString(),
        event: eventName,
        page_group: pageGroup,
        status,
        customData,
    });

    /* eslint-disable no-console */
    console.groupCollapsed(`[Bana] ${eventName} · "${pageGroup}" · ${businessModel} — ${status}`);
    if (customData) {
        console.table(customData);
    }
    console.groupEnd();
    /* eslint-enable no-console */
}

/**
 * Push a Bana event through the same queue and consent handling as the legacy events (held until the
 * cookie banner is answered), and log it. The data is built by the caller, so a queued push keeps the
 * values of this moment.
 * @param {string} eventName
 * @param {Object} customData
 * @param {Object} [options]
 * @param {boolean} [options.awaited] - Resolve only once GTM fired the tags (capped), for events before a redirect.
 * @returns {Promise<void>}
 */
function pushBanaEvent(eventName, customData, { awaited = false } = {}) {
    let status = BANA_STATUS.SENT;
    if (!window.gtmTrackingEnabled) {
        status = BANA_STATUS.GTM_DISABLED;
    } else if (!googleTagManager.hasCookieConsentForNewBanner()) {
        status = BANA_STATUS.QUEUED;
    }
    logBanaEvent(eventName, customData, status);

    const options = { customData, addUtmData: false, includePageData: false };
    if (awaited) {
        return googleTagManager.pushEventAwaited(eventName, options);
    }
    googleTagManager.pushEvent(eventName, options);
    return Promise.resolve();
}

/**
 * Bana page_view of a screen; a re-render of the same screen is not a new page view.
 * @param {string|null} pageGroup - One of BANA_PAGE_GROUP; null for a screen without a page view.
 * @param {string|null} businessModel - One of BUSINESS_MODEL; none on the general landing page.
 */
export function trackBanaPageView(pageGroup, businessModel) {
    if (!usesBanaTracking()) {
        return;
    }
    if (!pageGroup) {
        logBanaEvent('page_view', null, BANA_STATUS.NO_EVENT);
        return;
    }

    const screen = `${pageGroup}|${businessModel}`;
    if (screen === lastBanaScreen) {
        return;
    }
    lastBanaScreen = screen;

    pushBanaEvent('page_view', {
        ...getBanaPageData(pageGroup, businessModel),
        page_language: window.language.code,
        ...getBanaOriginalData(),
    });
}

/**
 * Bana business_model_click on a hero CTA of the general landing page.
 * @param {string} businessModel - One of BUSINESS_MODEL, the model of the clicked CTA.
 */
export function trackBanaBusinessModelClick(businessModel) {
    if (!usesBanaTracking()) {
        return;
    }

    pushBanaEvent('business_model_click', getBanaPageData(BANA_PAGE_GROUP.GENERAL_LANDING, businessModel));
}

/**
 * Bana apply_now_click on the apply CTA of the IC landing page.
 */
export function trackBanaApplyNowClick() {
    if (!usesBanaTracking()) {
        return;
    }

    pushBanaEvent('apply_now_click', getBanaPageData(BANA_PAGE_GROUP.LANDING, BUSINESS_MODEL.IC));
}

/**
 * Bana page_group of the multi-step form screen, like `step_tracking_name` for the legacy pageGroup.
 * @param {Object} screen
 * @param {boolean} screen.icFlow
 * @param {string} screen.step - The step id (location, what_you_need, personal_info, vehicle, complete).
 * @param {number|null} screen.fixedSubstep - Index of the fixed substep, null on a form question.
 * @param {string|null} screen.question - data_key of the form question on screen.
 * @param {boolean} screen.closedLocation - Closed IC zone or inactive employee city (waitlist).
 * @param {string} screen.dropoff - none, age or other (a rejecting answer).
 * @returns {string|null} One of BANA_PAGE_GROUP; null for a screen without a page view.
 */
export function getBanaFormPageGroup({ icFlow, step, fixedSubstep, question, closedLocation, dropoff }) {
    if (step === 'location') {
        return BANA_PAGE_GROUP.CITY_SELECTION;
    }
    if (step === 'what_you_need') {
        return BANA_PAGE_GROUP.WHAT_YOU_NEED;
    }

    if (icFlow) {
        if (step === 'personal_info') {
            return closedLocation
                ? BANA_PAGE_GROUP.WAITLIST_PERSONAL_INFORMATION
                : BANA_PAGE_GROUP.PERSONAL_INFORMATION;
        }
        if (step === 'vehicle') {
            return closedLocation ? BANA_PAGE_GROUP.WAITLIST_VEHICLE_SELECTION : BANA_PAGE_GROUP.VEHICLE_SELECTION;
        }
        if (step === 'complete') {
            return closedLocation ? BANA_PAGE_GROUP.WAITLIST_APPLICATION_SUMMARY : BANA_PAGE_GROUP.APPLICATION_SUMMARY;
        }
        return null;
    }

    if (step === 'personal_info' && fixedSubstep === 0) {
        return BANA_PAGE_GROUP.PERSONAL_INFORMATION;
    }
    if (step === 'personal_info' && fixedSubstep === 1) {
        return BANA_PAGE_GROUP.REFERRAL;
    }
    if (step === 'complete') {
        return dropoff === 'age' ? BANA_PAGE_GROUP.APPLICATION_SUMMARY_UNDER_AGE : BANA_PAGE_GROUP.APPLICATION_SUMMARY;
    }

    return BANA_EM_QUESTION_PAGE_GROUPS[question] ?? null;
}

/**
 * The outcome of a submission, or null for a rejection without a Bana event (a drop-off other than under age).
 * @param {{ icFlow: boolean, closedLocation: boolean, dropoff: string }} submission
 * @returns {string|null} One of BANA_OUTCOME.
 */
export function getBanaOutcome({ icFlow, closedLocation, dropoff }) {
    if (icFlow) {
        return closedLocation ? BANA_OUTCOME.IC_WAITLIST : BANA_OUTCOME.IC_SUCCESS;
    }
    if (closedLocation) {
        return BANA_OUTCOME.EM_CLOSED_CITY;
    }
    if (dropoff === 'age') {
        return BANA_OUTCOME.EM_UNDER_AGE;
    }

    return dropoff === 'none' ? BANA_OUTCOME.EM_SUCCESS : null;
}

/**
 * Bana application_submitted, at most once per application_id; also remembers the outcome for the
 * thank-you page view after the redirect. A success without a Salesforce applicant id is sent as
 * sf_failed, a rejected application sends no applicant_id, an under-age one no vehicle_type either,
 * and the IC waitlist sends no event.
 * @param {string|null} outcome - One of BANA_OUTCOME; null for a rejection without an event.
 * @param {Object} application
 * @param {string|null} application.city - tracking_name of the selected city.
 * @param {string|null} application.zone - Selected IC zone (zone_name); only sent on an IC application.
 * @param {string|null} application.vehicleType
 * @param {string|null} application.applicantId - Salesforce applicant id from the apply response.
 * @param {string|null} application.applicationId - Server-issued application_id from the apply response.
 * @returns {Promise<void>} Resolves when the event's tags executed or the cap elapsed; safe to await before a redirect.
 */
export function trackBanaApplicationSubmitted(outcome, { city, zone, vehicleType, applicantId, applicationId }) {
    if (!usesBanaTracking()) {
        return Promise.resolve();
    }
    setSessionItem(BANA_OUTCOME_KEY, outcome);

    const isSuccess = outcome === BANA_OUTCOME.IC_SUCCESS || outcome === BANA_OUTCOME.EM_SUCCESS;
    // A success only counts once Salesforce returned the applicant id
    const sfFailed = isSuccess && !applicantId;

    let pageData;
    switch (outcome) {
        case BANA_OUTCOME.IC_SUCCESS:
            pageData = getBanaPageData(
                sfFailed ? BANA_PAGE_GROUP.APPLICATION_SUBMITTED_SF_FAILED : BANA_PAGE_GROUP.APPLICATION_SUBMITTED,
                BUSINESS_MODEL.IC,
            );
            break;
        case BANA_OUTCOME.EM_SUCCESS:
            pageData = getBanaPageData(
                sfFailed
                    ? BANA_PAGE_GROUP.APPLICATION_SUBMITTED_SF_FAILED
                    : BANA_PAGE_GROUP.APPLICATION_SUBMITTED_SUCCESS,
                BUSINESS_MODEL.EM,
            );
            break;
        case BANA_OUTCOME.EM_CLOSED_CITY:
        case BANA_OUTCOME.EM_UNDER_AGE:
            pageData = getBanaPageData(BANA_PAGE_GROUP.APPLICATION_SUBMITTED_REJECTED, BUSINESS_MODEL.EM);
            break;
        default:
            logBanaEvent('application_submitted', null, BANA_STATUS.NO_EVENT);
            return Promise.resolve();
    }

    // One event per application, also when the page is re-rendered or the response replayed
    let trackedSubmissions;
    try {
        trackedSubmissions = JSON.parse(getSessionItem(BANA_TRACKED_SUBMISSIONS_KEY) ?? '[]');
    } catch {
        trackedSubmissions = [];
    }
    if (applicationId && trackedSubmissions.includes(applicationId)) {
        logBanaEvent('application_submitted', pageData, BANA_STATUS.DUPLICATE);
        return Promise.resolve();
    }
    if (applicationId) {
        setSessionItem(BANA_TRACKED_SUBMISSIONS_KEY, JSON.stringify([...trackedSubmissions, applicationId]));
    }

    return pushBanaEvent(
        'application_submitted',
        {
            ...pageData,
            application_city: city ?? null,
            ...(outcome === BANA_OUTCOME.IC_SUCCESS && { application_zone: zone ?? null }),
            ...(outcome !== BANA_OUTCOME.EM_UNDER_AGE && { vehicle_type: vehicleType ?? null }),
            ...(isSuccess && { applicant_id: applicantId ?? null }),
            application_id: applicationId ?? null,
        },
        { awaited: true },
    );
}

/**
 * Bana page_view of the thank-you page, for the outcome remembered before the redirect (removed on
 * read so it cannot leak into a later visit; a successful application goes to the Salesforce portal).
 */
export function trackBanaThankYou() {
    if (!usesBanaTracking()) {
        return;
    }

    const outcome = getSessionItem(BANA_OUTCOME_KEY);
    setSessionItem(BANA_OUTCOME_KEY, null);

    switch (outcome) {
        case BANA_OUTCOME.IC_WAITLIST:
            trackBanaPageView(BANA_PAGE_GROUP.WAITLIST_APPLICATION_SUBMITTED, BUSINESS_MODEL.IC);
            break;
        case BANA_OUTCOME.EM_CLOSED_CITY:
        case BANA_OUTCOME.EM_UNDER_AGE:
            trackBanaPageView(BANA_PAGE_GROUP.APPLICATION_SUBMITTED_REJECTED, BUSINESS_MODEL.EM);
            break;
        // A success only lands here when Salesforce failed; otherwise it goes to the Salesforce portal
        case BANA_OUTCOME.IC_SUCCESS:
            trackBanaPageView(BANA_PAGE_GROUP.APPLICATION_SUBMITTED_SF_FAILED, BUSINESS_MODEL.IC);
            break;
        case BANA_OUTCOME.EM_SUCCESS:
            trackBanaPageView(BANA_PAGE_GROUP.APPLICATION_SUBMITTED_SF_FAILED, BUSINESS_MODEL.EM);
            break;
    }
}
